import React, { useEffect, useMemo, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";

import { useAuth } from "../AuthProvider";
import MiniBacktestPanel from "./MiniBacktestPanel";
import TimingCheckPanel from "./TimingCheckPanel";
import BrokerSummaryCard from "./BrokerSummaryCard";
import StockDetailSummaryBar from "./StockDetailSummaryBar";
import { ASK_AI_TYPES } from "../utils/askAi/askAiTypes";
import { buildAskAiPrompt } from "../utils/askAi/buildAskAiPrompt";

const useToast = () => {
  const [msg, setMsg] = React.useState(null);
  const show = (m, ms = 1800) => {
    setMsg(m);
    setTimeout(() => setMsg(null), ms);
  };
  const Toast = () =>
    msg ? (
      <div className="fixed top-4 right-4 z-[1000] px-3 py-2 rounded bg-green-600 text-white shadow">
        {msg}
      </div>
    ) : null;
  return { show, Toast };
};

const fmtNum = (n) => (n != null ? Number(n).toLocaleString("id-ID") : "-");
const fmtInt = (n) =>
  n != null ? Number(n).toLocaleString("id-ID", { maximumFractionDigits: 0 }) : "-";
const fmtPct = (n) =>
  n != null ? `${Number(n).toFixed(2)}%` : "-";

const getPctClass = (n) => {
  if (n == null) return "text-gray-300";
  const v = Number(n);
  if (isNaN(v)) return "text-gray-300";
  if (v < 0) return "text-red-400";
  if (v > 0) return "text-green-400";
  return "text-gray-300";
};

export default function StocksPanel({ 
  onAskAi,
  openAskChat,
  category,
  setCategory, 
  groupBy,
  setGroupBy,
  categories,
  forceSelectedCode, 
  hideListFilters,
}) {

  const { token } = useAuth() || {};

  const authHeaders = () => {
    const h = { "Content-Type": "application/json" };
    if (token) h["Authorization"] = `Bearer ${token}`;
    return h;
  };

  const groupByOptions = [
    { value: "all", label: "All Stocks" },
    { value: "broker", label: "Broker" },
	{ value: "industry", label: "Industri" },
    { value: "konglo", label: "Konglo" },
  ];
	
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showLoadingUi, setShowLoadingUi] = useState(false);

  const [brokerRows, setBrokerRows] = useState([]);
  const [brokerLoading, setBrokerLoading] = useState(false);
  const [brokerError, setBrokerError] = useState("");
  const [brokerDate, setBrokerDate] = useState(""); // empty = latest date from backend

  const [view, setView] = useState("list"); // 'list' | 'detail'
  const [selectedCode, setSelectedCode] = useState(null);
  const [detailRows, setDetailRows] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailYear, setDetailYear] = useState(2026);
  const [detailDays, setDetailDays] = useState(30);
  const [limitDays, setLimitDays] = useState(30);
  const [showBackOnDetail, setShowBackOnDetail] = useState(false);

  const isForcedDetail = !!forceSelectedCode;

  const [detailMetaRow, setDetailMetaRow] = useState(null);
  const [detailMetaLoading, setDetailMetaLoading] = useState(false);

  const actualSelectedCode = forceSelectedCode || selectedCode;

  const [search, setSearch] = useState("");
  const [watchlistSet, setWatchlistSet] = useState(() => new Set());
  const [isStarred, setIsStarred] = useState(false);
  const [starLoading, setStarLoading] = useState(false);
  
  const [signalFilter, setSignalFilter] = useState("ALL"); // ALL | BUY | WATCH | NEUTRAL | AVOID
  const [stockTypeFilter, setStockTypeFilter] = useState("ALL"); // ALL | BLUECHIP | MEDIUM | SMALL | GORENGAN
  
  const [sortKey, setSortKey] = useState("suggest_pct");   // suggest_pct | rr | chg_4w_pct | chg_13w_pct | code
  const [sortDir, setSortDir] = useState("desc");          // asc | desc
  
  const { show, Toast } = useToast();

  const [mobileView, setMobileView] = useState("stocks");
  const [showMobileFilters, setShowMobileFilters] = useState(false);

  const [summaryRow, setSummaryRow] = useState(null);	
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [industries, setIndustries] = useState([]);
  const [selectedIndustry, setSelectedIndustry] = useState("");
  const [industrySummaryRows, setIndustrySummaryRows] = useState([]);
	
  const [subindustries, setSubindustries] = useState([]);
  const [selectedSubindustry, setSelectedSubindustry] = useState(""); 
  const [subindustrySummaryRows, setSubindustrySummaryRows] = useState([]);

  const [kongloSummaryRows, setKongloSummaryRows] = useState([]);
  const [selectedKonglo, setSelectedKonglo] = useState("");

  const { code: routeCode } = useParams();
  const nav = useNavigate();

  
  // ==========================
  // Helpers
  // ==========================

  function DesktopStockTableSkeleton({ rows = 10 }) {
    return (
      <div className="rounded-xl border border-gray-800 bg-gray-900/60 overflow-hidden animate-pulse">
        <div className="px-4 py-3 border-b border-gray-800 text-sm text-gray-400">
          Memuat data saham...
        </div>

        <div className="hidden md:grid grid-cols-12 gap-3 px-4 py-3 border-b border-gray-800 text-xs text-gray-500">
          <div className="col-span-2">Code</div>
          <div className="col-span-3">Name</div>
          <div className="col-span-2">Price</div>
          <div className="col-span-2">Change</div>
          <div className="col-span-3">Signal</div>
        </div>

        {Array.from({ length: rows }).map((_, i) => (
          <div
            key={i}
            className="hidden md:grid grid-cols-12 gap-3 px-4 py-3 border-b border-gray-800 items-center"
          >
            <div className="col-span-2 h-4 rounded bg-gray-800" />
            <div className="col-span-3 h-4 rounded bg-gray-800" />
            <div className="col-span-2 h-4 rounded bg-gray-800" />
            <div className="col-span-2 h-4 rounded bg-gray-800" />
            <div className="col-span-3 h-4 rounded bg-gray-800" />
          </div>
        ))}
      </div>
    );
  }

  function MobileStockCardSkeleton({ rows = 6 }) {
    return (
      <div className="space-y-4">
        {Array.from({ length: rows }).map((_, i) => (
          <div
            key={i}
            className="rounded-2xl border border-slate-700/70 bg-[#071b2b]/90 px-4 py-4 shadow-[0_0_0_1px_rgba(255,255,255,0.02)] animate-pulse"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1 space-y-3">
                <div className="flex items-center gap-2">
                  <div className="h-6 w-16 rounded bg-slate-700" />
                  <div className="h-6 w-14 rounded-full bg-slate-700" />
                </div>

                <div className="space-y-2">
                  <div className="h-4 w-40 rounded bg-slate-700" />
                  <div className="h-4 w-44 rounded bg-slate-700" />
                  <div className="h-4 w-28 rounded bg-slate-700" />
                  <div className="h-4 w-36 rounded bg-slate-700" />
                </div>

                <div className="h-4 w-24 rounded bg-slate-700" />
              </div>

              <div className="w-[96px] shrink-0 space-y-3 text-right">
                <div className="ml-auto h-4 w-20 rounded bg-slate-700" />
                <div className="ml-auto h-5 w-16 rounded bg-slate-700" />
                <div className="ml-auto h-5 w-16 rounded bg-slate-700" />
                <div className="ml-auto h-4 w-12 rounded bg-slate-700" />
              </div>
            </div>

            <div className="mt-4 flex justify-end">
              <div className="h-10 w-24 rounded-xl bg-slate-700" />
            </div>
          </div>
        ))}
      </div>
    );
  }  

  function StocksLoadingSkeleton() {
    return (
      <>
        <div className="md:hidden">
          <MobileStockCardSkeleton rows={5} />
        </div>

        <div className="hidden md:block">
          <DesktopStockTableSkeleton rows={10} />
        </div>
      </>
    );
  }

  // round down angka
  function fmtFloor(n) {
    if (n == null) return "-";
    return Math.floor(Number(n)).toString();
  }
  
  // Ambil low & high dari buy range (support 2 bentuk: buy_min/buy_max atau string "x - y")
  function extractBuyRange(row) {
    if (row.buy_min != null && row.buy_max != null) {
      return [Number(row.buy_min), Number(row.buy_max)];
    }
  
    if (typeof row.buy_range === "string") {
      const parts = row.buy_range.split("-");
      if (parts.length === 2) {
        const low = parseFloat(parts[0].trim());
        const high = parseFloat(parts[1].trim());
        if (!Number.isNaN(low) && !Number.isNaN(high)) {
          return [low, high];
        }
      }
    }
  
    return [null, null];
  }

function getTimingStatus(row) {
  const last = Number(row.last_price);
  if (!last || Number.isNaN(last)) {
    return { label: "-", className: "text-gray-500" };
  }

  const lastDate = row.last_date;
  const closeDate = row.trade_date;

  // ❗ Timing hanya aktif kalau last_date > closeDate
  // (snapshot lebih baru dari data close yang di-upload)
  if (!lastDate || !closeDate || lastDate <= closeDate) {
    return { label: "-", className: "text-gray-500" };
  }

  const [low, high] = extractBuyRange(row);
  if (low == null || high == null) {
    return { label: "-", className: "text-gray-500" };
  }

  const tp1 = row.tp1 != null ? Number(row.tp1) : null;
  const tp2 = row.tp2 != null ? Number(row.tp2) : null;

  // 1) Dalam Buy Range → OK ENTRY
  if (last >= low && last <= high) {
    return {
      label: "OK ENTRY",
      className: "text-emerald-400 font-semibold",
    };
  }

  // 2) Di bawah Buy Range
  if (last < low) {
    const diffPct = ((low - last) / low) * 100; // berapa % di bawah low

    // Di bawah 3% dari low → SKIP
    if (diffPct > 3) {
      return {
        label: "SKIP, PRICE DROP",
        className: "text-red-400 font-semibold",
      };
    }

    // 0–3% di bawah low → PRICE DROP (caution turun)
    return {
      label: "CAUTION, PRICE DROP",
      className: "text-orange-400 font-semibold",
    };
  }

  // 3) Di atas Buy Range
  if (last > high) {
    // Di atas TP2 → SKIP
    if (tp2 && last > tp2) {
      return {
        label: "SKIP, PRICE TOO HIGH",
        className: "text-red-400 font-semibold",
      };
    }

    // Di atas high & di bawah TP1 → OUT OF BUY RANGE (caution naik)
    if (tp1 && last < tp1) {
      return {
        label: "CAUTION, PRICE UP",
        className: "text-yellow-400 font-semibold",
      };
    }

    // Kalau tidak ada TP1/TP2 atau di zona abu-abu lain → tetap treat sebagai OUT OF BUY RANGE
    return {
      label: "OUT OF BUY RANGE",
      className: "text-red-400 font-semibold",
    };
  }

  // fallback (harusnya jarang kena)
  return {
    label: "-",
    className: "text-gray-500",
  };
}


  // refresh watchlist
  const refreshWatchlistSet = async () => {
    try {
      const r = await fetch("/api/stocks/watchlist?active=1&limit=2000");
      const js = await r.json();
      const syms = (Array.isArray(js) ? js : [])
        .map((x) => x.symbol)
        .filter(Boolean);
      setWatchlistSet(new Set(syms.map((s) => s.toUpperCase())));
    } catch (e) {
      console.warn("refreshWatchlistSet error:", e);
    }
  };

  const rowKey = (r, idx) => r.id ?? r.symbol ?? r.code ?? `row-${idx}`;

  const EyeIcon = () => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      className="inline h-4 w-4"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );

  const makeTvUrl = (code) => {
    const symbol = `IDX:${code.toUpperCase()}`;
    const params = new URLSearchParams({
      symbol,
      interval: "D",
      theme: "dark",
      style: "1",
      timezone: "Asia/Jakarta",
      studies: "[]",
      toolbarbg: "1f2937",
      locale: "id",
      showpopupbutton: "1",
      saveimage: "0",
      enabled_features: "[]",
      disabled_features: "[]",
    });
    return `https://s.tradingview.com/widgetembed/?${params.toString()}`;
  };

  
  // ==========================
  // Backend calls
  // ==========================

  const engineFromCategory = (cat) => {
    if (cat === "bandar_v2") return "v2";
    if (cat === "value") return "value"; // nanti
    return "v1";
  };

  const fetchAllWithEngine = async () => {
    const engine = engineFromCategory(category);
    const res = await fetch(`/api/stocks/all?engine=${engine}`);
    const data = await res.json();
    return data.rows || [];
  };

	
  const allAbortRef = useRef(null);
  const allReqIdRef = useRef(0);

  const fetchAllStocks = async () => {
    const reqId = ++allReqIdRef.current;

    // batalkan request sebelumnya
    if (allAbortRef.current) {
      allAbortRef.current.abort();
    }

    const controller = new AbortController();
    allAbortRef.current = controller;

    setLoading(true);

    try {
      const engine = engineFromCategory(category);
      const res = await fetch(`/api/stocks/all?engine=${engine}`, {
        signal: controller.signal,
      });

      const data = await res.json();

      if (reqId !== allReqIdRef.current) return;

      setRows(data.rows || []);
    } catch (e) {
      if (e.name === "AbortError") {
        return;
      }

      if (reqId !== allReqIdRef.current) return;
      console.error("fetchAllStocks error:", e);
    } finally {
      if (reqId !== allReqIdRef.current) return;
      setLoading(false);
    }
  };

  const fetchBrokerFollower = async (selectedDate = brokerDate) => {
    setBrokerLoading(true);
    setBrokerError("");

    const normalizedDate = selectedDate || "";
    const queryString = normalizedDate ? `?date=${encodeURIComponent(normalizedDate)}` : "";

    try {
      const endpoints = [
        `/api/stocks/broker-follower${queryString}`,
        `/broker-follower${queryString}`,
      ];
      let data = null;
      let lastError = null;

      for (const endpoint of endpoints) {
        try {
          const res = await fetch(endpoint, { headers: authHeaders() });
          const json = await res.json().catch(() => ({}));

          if (!res.ok) {
            lastError = new Error(json?.detail || json?.message || `HTTP ${res.status}`);
            continue;
          }

          data = json;
          break;
        } catch (err) {
          lastError = err;
        }
      }

      if (!data) throw lastError || new Error("Broker Follower endpoint not available");

      const arr = Array.isArray(data) ? data : (data.data || data.rows || []);
      setBrokerRows(arr);
    } catch (e) {
      console.error("fetchBrokerFollower error:", e);
      setBrokerError(e?.message || "Gagal load Broker Follower");
      setBrokerRows([]);
    } finally {
      setBrokerLoading(false);
    }
  };

  const handleLoadBrokerDate = () => {
    fetchBrokerFollower(brokerDate);
  };

  const handleLoadLatestBroker = () => {
    setBrokerDate("");
    fetchBrokerFollower("");
  };

	
  const loadWatchlist = async () => {
    setLoading(true);
    try {
      // 1) ensure historical data (opsional, tetap pakai pipeline lama)
      const ensure = await fetch("/api/stocks/ensure-30d", { method: "POST" });
      if (!ensure.ok) {
        const err = await ensure.json().catch(() => ({}));
        console.warn("ensure-30d failed:", err);
      }

      // 2) recompute bandarmology untuk watchlist
      const rec = await fetch("/api/bandarmology/recompute", {
        method: "POST",
      });
      if (!rec.ok) {
        const err = await rec.json().catch(() => ({}));
        console.warn("recompute failed:", err);
      }

      // 3) ambil tabel watchlist
      const res = await fetch(
        `/api/stocks/watchlist?active=1&order=score_desc`
      );
      const json = await res.json();
      const arr = Array.isArray(json) ? json : [];
      setRows(arr.filter((x) => x && x.symbol));

      refreshWatchlistSet();
    } catch (e) {
      console.error("loadWatchlist error:", e);
    } finally {
      setLoading(false);
    }
  };

  const fetchWatchlistSet = async () => {
    try {
      const headers = {};
      if (token) headers["Authorization"] = `Bearer ${token}`;

      const r = await fetch("/api/stocks/watchlist?active_only=1", { headers });
      const js = await r.json();
      if (!r.ok || js?.status !== "ok") throw new Error(js?.detail || js?.message || "Failed");

      const s = new Set((js.rows || []).map(x => (x.symbol || "").toUpperCase().trim()));
      setWatchlistSet(s);
    } catch (e) {
      console.error(e);
      setWatchlistSet(new Set());
    }
  };

  const fetchDetail = async (code, year, days) => {
    setDetailLoading(true);
    try {
      const qs = new URLSearchParams({
        code,
        year: String(year),
        days: String(days),
      }).toString();
      const res = await fetch(`/api/stocks/detail/ensure?${qs}`, {
        method: "POST",
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || "Failed ensure detail");
      setDetailRows(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error(e);
      alert(`Gagal ambil detail: ${e.message || e}`);
      setDetailRows([]);
    } finally {
      setDetailLoading(false);
    }
  };

const fetchDetailMetaRow = async (code) => {
  const sym = String(code || "").toUpperCase().trim();
  if (!sym) return;

  setDetailMetaLoading(true);
  try {
    const qs = new URLSearchParams({
      code: sym,
      engine: "v1",
      sort_by: "suggest_pct",
      sort_dir: "desc",
    });

    const r = await fetch(`/api/stocks/all?${qs.toString()}`, {
      headers: authHeaders(),
    });

    const js = await r.json();
    const row = js?.rows?.[0] || null;

    setDetailMetaRow(row);
  } catch (e) {
    console.error("fetchDetailMetaRow error:", e);
    setDetailMetaRow(null);
  } finally {
    setDetailMetaLoading(false);
  }
};	

const fetchSummaryRow = async (code) => {
  const sym = String(code || "").toUpperCase().trim();
  if (!sym) return;

  setSummaryLoading(true);

  try {
    const qs = new URLSearchParams({
      code: sym,
      engine: "v1",
      sort_by: "suggest_pct",
      sort_dir: "desc",
    });

    const r = await fetch(`/api/stocks/all?${qs.toString()}`, {
      headers: authHeaders(),
    });

    if (!r.ok) {
      console.error("fetchSummaryRow failed:", r.status);
      setSummaryRow(null);
      return;
    }

    const js = await r.json();

    // kalau backend sudah support ?code= -> rows cuma 1
    let row = js?.rows?.[0] || null;

    // fallback kalau backend belum difilter (ambil dari 900 rows)
    if (!row && Array.isArray(js?.rows)) {
      row = js.rows.find(
        (x) => String(x.code || "").toUpperCase() === sym
      ) || null;
    }

    setSummaryRow(row);
  } catch (err) {
    console.error("fetchSummaryRow error:", err);
    setSummaryRow(null);
  } finally {
    setSummaryLoading(false);
  }
};

  const mapSortKeyToApi = (key) => {
    if (key === "chg_4w_pct") return "chg_4w_pct";
    if (key === "chg_13w_pct") return "chg_13w_pct";
    if (key === "code") return "kode_saham";
    // default: pakai 4W
    return "chg_4w_pct";
  };

	const fetchIndustries = async () => {
	  try {
	    const res = await fetch("/api/stocks/industries");
	    if (!res.ok) throw new Error("Failed to fetch industries");
	    const data = await res.json(); // expect: ["Energy", "Technology", ...]
	    setIndustries(data);
	  } catch (err) {
	    console.error(err);
	    show("Gagal load industri");
	  }
	};	

  const fetchSubindustries = async () => {
    try {
      const res = await fetch("/api/stocks/subindustries");
      if (!res.ok) throw new Error("Failed to fetch subindustries");
      const data = await res.json(); // expect: ["Coal Production", "Plantations & Crops", ...]
      setSubindustries(data);
    } catch (err) {
      console.error(err);
      show("Gagal load subindustri");
    }
  };

  const [industryRows, setIndustryRows] = useState([]);

  const fetchStocksByIndustry = async () => {
	  if (!selectedIndustry) {
	    setIndustryRows([]);
	    return;
	  }
	
	  setLoading(true);
	  try {
	    const allRows = await fetchAllWithEngine();
	
	    const filtered = allRows.filter(
	      (row) => row.industri === selectedIndustry
	    );
	
	    setIndustryRows(filtered);
	  } catch (e) {
	    console.error("fetchStocksByIndustry error:", e);
	    show("Gagal load saham industri");
	  } finally {
	    setLoading(false);
	  }
	};
	
	const fetchStocksBySubindustry = async () => {
	  if (!selectedSubindustry) {
	    setRows([]);
	    return;
	  }
	
	  setLoading(true);
	  try {
	    const allRows = await fetchAllWithEngine();
	
	    const filtered = allRows.filter(
	      (row) => row.subindustri === selectedSubindustry
	    );
	
	    setRows(filtered);
	  } catch (e) {
	    console.error("fetchStocksBySubindustry error:", e);
	    show("Gagal load saham subindustri");
	  } finally {
	    setLoading(false);
	  }
	};

	const toNum = (v) => {
	  if (v == null) return 0;
	  if (typeof v === "number") return Number.isFinite(v) ? v : 0;
	  const s = String(v).trim();
	  if (!s) return 0;
	  const cleaned = s.replace(/\./g, "").replace(/,/g, ".");
	  const n = Number(cleaned);
	  return Number.isFinite(n) ? n : 0;
	};
	
	const fetchIndustrySummary = async () => {
	  setLoading(true);
	  try {
	    const allRows = await fetchAllWithEngine();
	    const map = new Map();
	
	    for (const row of allRows) {
	      const key = row.industri || "Unknown";
	      if (!key) continue;
	
	      let g = map.get(key);
	      if (!g) {
	        g = {
	          industri: key,
	          count_buy: 0,
	          count_watch: 0,
	          count_neutral: 0,
	          count_avoid: 0,
	          sum_4w: 0,
	          sum_13w: 0,
	          cnt_4w: 0,
	          cnt_13w: 0,
	          total_value: 0,
	          total_fbuy: 0,
	          total_fsell: 0,
	        };
	        map.set(key, g);
	      }
	
	      const sig = (row.signal || "").toUpperCase();
	      if (sig === "BUY") g.count_buy++;
	      else if (sig === "WATCH") g.count_watch++;
	      else if (sig === "NEUTRAL") g.count_neutral++;
	      else if (sig === "AVOID") g.count_avoid++;
	
	      const v4 = toNum(row.chg_4w_pct);
	      if (v4 !== 0 || row.chg_4w_pct != null) { g.sum_4w += v4; g.cnt_4w++; }
	
	      const v13 = toNum(row.chg_13w_pct);
	      if (v13 !== 0 || row.chg_13w_pct != null) { g.sum_13w += v13; g.cnt_13w++; }
	
	      g.total_value += toNum(row.value);
	      g.total_fbuy  += toNum(row.foreign_buy);
	      g.total_fsell += toNum(row.foreign_sell);
	    }
	
	    const summary = Array.from(map.values()).map((g) => ({
	      ...g,
	      avg_4w_pct: g.cnt_4w ? g.sum_4w / g.cnt_4w : null,
	      avg_13w_pct: g.cnt_13w ? g.sum_13w / g.cnt_13w : null,
	      foreign_net: g.total_fbuy - g.total_fsell,
	    }));
	
	    const getSortValue = (x) => {
	      switch (sortKey) {
	        case "foreign_net": return toNum(x.foreign_net);
	        case "total_value": return toNum(x.total_value);
	        case "chg_4w_pct":  return toNum(x.avg_4w_pct);
	        case "chg_13w_pct": return toNum(x.avg_13w_pct);
	        case "code":        return String(x.industri ?? "");
	        default:            return toNum(x.avg_4w_pct);
	      }
	    };
	
	    summary.sort((a, b) => {
	      const av = getSortValue(a);
	      const bv = getSortValue(b);
	
	      if (sortKey === "code") {
	        return sortDir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
	      }
	
	      // handle NaN safety (shouldn't happen now, but extra safe)
	      const an = Number.isFinite(av) ? av : 0;
	      const bn = Number.isFinite(bv) ? bv : 0;
	      return sortDir === "asc" ? an - bn : bn - an;
	    });
	
	    setIndustrySummaryRows(summary);
	  } catch (e) {
	    console.error("fetchIndustrySummary error:", e);
	    show("Gagal load ringkasan industri");
	  } finally {
	    setLoading(false);
	  }
	};
	
  	const fetchSubindustrySummary = async () => {
	  setLoading(true);
	  try {
	    const allRows = await fetchAllWithEngine();
	    const map = new Map();
	
	    for (const row of allRows) {
	      const key = row.subindustri || "Unknown";
	      if (!key) continue;
	
	      let g = map.get(key);
	      if (!g) {
	        g = {
	          subindustri: key,
	          count_buy: 0,
	          count_watch: 0,
	          count_neutral: 0,
	          count_avoid: 0,
	          sum_4w: 0,
	          sum_13w: 0,
	          cnt_4w: 0,
	          cnt_13w: 0,
	          total_value: 0,
	          total_fbuy: 0,
	          total_fsell: 0,
	        };
	        map.set(key, g);
	      }
	
	      const sig = (row.signal || "").toUpperCase();
	      if (sig === "BUY") g.count_buy++;
	      else if (sig === "WATCH") g.count_watch++;
	      else if (sig === "NEUTRAL") g.count_neutral++;
	      else if (sig === "AVOID") g.count_avoid++;
	
	      const v4 = Number(row.chg_4w_pct);
	      if (!isNaN(v4)) { g.sum_4w += v4; g.cnt_4w++; }
	
	      const v13 = Number(row.chg_13w_pct);
	      if (!isNaN(v13)) { g.sum_13w += v13; g.cnt_13w++; }
	
	      g.total_value += Number(row.value ?? 0);
	      g.total_fbuy += Number(row.foreign_buy ?? 0);
	      g.total_fsell += Number(row.foreign_sell ?? 0);
	    }
	
	    const summary = Array.from(map.values()).map(g => ({
	      ...g,
	      avg_4w_pct: g.cnt_4w ? g.sum_4w / g.cnt_4w : null,
	      avg_13w_pct: g.cnt_13w ? g.sum_13w / g.cnt_13w : null,
	      foreign_net: g.total_fbuy - g.total_fsell,
	    }));
	
	    summary.sort((a, b) => (b.avg_4w_pct ?? 0) - (a.avg_4w_pct ?? 0));
	
	    setSubindustrySummaryRows(summary);
	    setRows([]);
	  } catch (e) {
	    console.error("fetchSubindustrySummary error:", e);
	    show("Gagal load ringkasan subindustri");
	  } finally {
	    setLoading(false);
	  }
	};


	const fetchKongloSummary = async () => {
	  setLoading(true);
	  try {
	    const allRows = await fetchAllWithEngine();
	    const map = new Map();
	
	    for (const row of allRows) {
	      const key = row.konglo_owner;
	      if (!key) continue;
	
	      let g = map.get(key);
	      if (!g) {
	        g = {
	          konglo: key,
	          count_buy: 0,
	          count_watch: 0,
	          count_neutral: 0,
	          count_avoid: 0,
	          sum_4w: 0,
	          sum_13w: 0,
	          cnt_4w: 0,
	          cnt_13w: 0,
	          total_value: 0,
	          total_fbuy: 0,
	          total_fsell: 0,
	        };
	        map.set(key, g);
	      }
	
	      const sig = (row.signal || "").toUpperCase();
	      if (sig === "BUY") g.count_buy++;
	      else if (sig === "WATCH") g.count_watch++;
	      else if (sig === "NEUTRAL") g.count_neutral++;
	      else if (sig === "AVOID") g.count_avoid++;
	
	      const v4 = Number(row.chg_4w_pct);
	      if (!isNaN(v4)) { g.sum_4w += v4; g.cnt_4w++; }
	
	      const v13 = Number(row.chg_13w_pct);
	      if (!isNaN(v13)) { g.sum_13w += v13; g.cnt_13w++; }
	
	      g.total_value += Number(row.value ?? 0);
	      g.total_fbuy += Number(row.foreign_buy ?? 0);
	      g.total_fsell += Number(row.foreign_sell ?? 0);
	    }
	
	    const summary = Array.from(map.values()).map(g => ({
	      ...g,
	      avg_4w_pct: g.cnt_4w ? g.sum_4w / g.cnt_4w : null,
	      avg_13w_pct: g.cnt_13w ? g.sum_13w / g.cnt_13w : null,
	      foreign_net: g.total_fbuy - g.total_fsell,
	    }));
	
	    summary.sort((a, b) => (b.avg_4w_pct ?? 0) - (a.avg_4w_pct ?? 0));
	
	    setKongloSummaryRows(summary);
	  } catch (e) {
	    console.error("fetchKongloSummary error:", e);
	    show("Gagal load rekap Konglo");
	  } finally {
	    setLoading(false);
	  }
	};

  const [kongloRows, setKongloRows] = useState([]);

  const fetchStocksByKonglo = async () => {
    if (!selectedKonglo) {
      setKongloRows([]);   // ✅ bukan setRows([])
      return;
    }

    setLoading(true);
    try {
      const allRows = await fetchAllWithEngine();
      const filtered = allRows.filter((row) => row.konglo_owner === selectedKonglo);
      setKongloRows(filtered); // ✅ bukan setRows
    } catch (e) {
      console.error("fetchStocksByKonglo error:", e);
      show("Gagal load saham Konglo");
    } finally {
      setLoading(false);
    }
  };

  const handleClickCode = (code) => {
    const sym = (code || "").toUpperCase().trim();
    if (!sym) return;

    const isDesktop = window.matchMedia("(min-width: 768px)").matches;

    if (isDesktop) {
      setShowBackOnDetail(true);   // ✅ marker ON (karena dari table)
      setSelectedCode(sym);        // 🔥 buka detail di panel kanan
    } else {
      // mobile route (kalau kamu mau juga muncul Back di route ketika klik table)
      nav(`/stocks/${encodeURIComponent(sym)}`, { state: { from: "table" } });
    }
  };

  const handleBack = () => {
    setView("list");
    setSelectedCode(null);
    setDetailRows([]);
  };

  const handleAdd = async (codeOrSymbol) => {
    const symbol = (codeOrSymbol || "").toUpperCase();
    if (!symbol) return;

    try {
      const r = await fetch(
        `/api/stocks/watchlist/${encodeURIComponent(symbol)}?is_active=1`,
        { method: "POST", headers: authHeaders() }
      );
      const js = await r.json();
      if (!r.ok || js?.status !== "ok") throw new Error(js?.detail || js?.message || "Add failed");

      setWatchlistSet((prev) => new Set(prev).add(symbol));
    } catch (e) {
      console.error(e);
      alert(`Gagal menambahkan ${symbol} ke Watchlist`);
    }
  };

  const handleRemove = async (symbol) => {
    const sym = (symbol || "").toUpperCase();
    if (!sym) return;

    const ok = window.confirm(`Hapus ${sym} dari Watchlist?`);
    if (!ok) return;

    try {
      const r = await fetch(
        `/api/stocks/watchlist/${encodeURIComponent(sym)}?hard=0`,
        { method: "DELETE", headers: authHeaders() }
      );
      const js = await r.json();
      if (!r.ok || js?.status !== "ok") throw new Error(js?.detail || js?.message || "Failed");

      setWatchlistSet((prev) => {
        const s = new Set(prev);
        s.delete(sym);
        return s;
      });
    } catch (e) {
      console.error(e);
      alert("Gagal remove. Cek log.");
    }
  };

  const handleRemoveSilent = async (symbol) => {
    const sym = (symbol || "").toUpperCase();
    if (!sym) return;
    try {
      const r = await fetch(
        `/api/stocks/watchlist/${encodeURIComponent(sym)}?hard=0`,
        { method: "DELETE", headers: authHeaders() }   // ✅ tambah token
      );
      const js = await r.json();
      if (!r.ok || js?.status !== "ok") throw new Error(js?.detail || js?.message || "Failed");

      show(`${sym} removed from Watchlist 🗑️`);
      setWatchlistSet((prev) => {
        const s = new Set(prev);
        s.delete(sym);
        return s;
      });
    } catch (e) {
      console.error(e);
      alert("Gagal remove. Cek log.");
    }
  };

	const handleRefresh = () => {
	  if (groupBy === "all") {
	    fetchAllStocks();
	  } else if (groupBy === "broker") {
	    fetchBrokerFollower();
	  } else if (groupBy === "industry") {
	    if (selectedIndustry) fetchStocksByIndustry();
	    else fetchIndustrySummary();
	  } else if (groupBy === "konglo") {
	    if (selectedKonglo) fetchStocksByKonglo();
	    else fetchKongloSummary();
	  }
	};

  const displayRows = useMemo(() => {
    if (groupBy === "all") return rows;

    if (groupBy === "broker") return brokerRows;

    if (groupBy === "konglo") {
      return selectedKonglo ? kongloRows : kongloSummaryRows;
    }

    if (groupBy === "industry") {
      return selectedIndustry ? industryRows : industrySummaryRows;
    }

    return [];
  }, [
    groupBy,
    rows,
    brokerRows,
    selectedKonglo,
    kongloRows,
    kongloSummaryRows,
    selectedIndustry,
    industryRows,
    industrySummaryRows,
  ]);  

  const processedRows = useMemo(() => {
    let arr = [...(displayRows || [])];

    // ✅ Search untuk All Stocks dan Broker Follower
    if (groupBy === "all" || groupBy === "broker") {
      const q = search.trim().toUpperCase();
      if (q) {
        arr = arr.filter((r) => {
          const code = (r.symbol || r.code || r.kode_saham || "").toUpperCase();
          return code.includes(q);
        });
      }
    }

    // Filter by signal (All Stocks view)
    if (groupBy === "all" && signalFilter !== "ALL") {
      arr = arr.filter((r) => (r.signal || "").toUpperCase() === signalFilter);
    }

    // Filter by stock type (All Stocks view)
    if (groupBy === "all" && stockTypeFilter !== "ALL") {
      arr = arr.filter((r) => (String(r.stock_type || "").toUpperCase() === stockTypeFilter));
    }

    if (groupBy === "broker") return arr;

    // Sorting
    arr.sort((a, b) => {
      const dir = sortDir === "asc" ? 1 : -1;

      // code sorting works for all modes (summary/detail)
      if (sortKey === "code") {
        const ca = (a.code || a.symbol || a.industri || a.konglo || "").toUpperCase();
        const cb = (b.code || b.symbol || b.industri || b.konglo || "").toUpperCase();
        return ca > cb ? dir : ca < cb ? -dir : 0;
      }

      const va = Number(a[sortKey] ?? 0);
      const vb = Number(b[sortKey] ?? 0);
      if (va === vb) return 0;
      return va > vb ? dir : -dir;
    });

    return arr;
  }, [
    displayRows,
    groupBy,
    search,
    signalFilter,
    stockTypeFilter,
    sortKey,
    sortDir,
  ]);  

  // ==========================
  // Pagination
  // ==========================
  const [pageSize, setPageSize] = useState("10"); // "10" | "ALL"
  const [page, setPage] = useState(1);

  const totalRows = processedRows.length;

  // Reset ke page 1 kalau filter / data berubah atau pageSize berubah
  useEffect(() => {
    setPage(1);
  }, [category, groupBy, search, signalFilter, stockTypeFilter, sortKey, sortDir, pageSize, totalRows]);

  const pageSizeNum =
    pageSize === "ALL" ? (totalRows === 0 ? 1 : totalRows) : Number(pageSize);

  const totalPages =
    pageSize === "ALL"
      ? 1
      : Math.max(1, Math.ceil(totalRows / pageSizeNum));

  const startIndex = pageSize === "ALL" ? 0 : (page - 1) * pageSizeNum;
  const endIndex =
    pageSize === "ALL" ? totalRows : Math.min(startIndex + pageSizeNum, totalRows);

  const pagedRows =
    pageSize === "ALL"
      ? processedRows
      : processedRows.slice(startIndex, endIndex);

  const goPrev = () => {
    setPage((p) => Math.max(1, p - 1));
  };

  const goNext = () => {
    setPage((p) => Math.min(totalPages, p + 1));
  };    

  const SearchRefresh = ({ search, setSearch, onRefresh, loading }) => (
    <div className="flex items-end gap-2">
      <input
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Search (ACES, GZCO...)"
        className="h-9 w-56 border border-gray-600 bg-gray-900 text-gray-200 rounded px-3 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
      />
      <button
        onClick={onRefresh}
        className="h-9 px-3 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded whitespace-nowrap"
        disabled={loading}
        title="Refresh"
      >
        {loading ? "…" : "Refresh"}
      </button>
    </div>
  );

	const PaginationBar = ({ className = "" }) => (
	  totalRows > 0 && (
	    <div
	      className={`
	        mt-3 flex flex-col gap-2 text-xs text-gray-300
	        items-center text-center
	        md:flex-row md:justify-between md:items-center md:text-left
	        ${className}
	      `}
	    >
	      {/* LEFT / TOP */}
	      <div className="flex flex-wrap items-center justify-center gap-2">
	        <span>Rows:</span>
	        <select
	          value={pageSize}
	          onChange={(e) => setPageSize(e.target.value)}
	          className="bg-gray-800 border border-gray-700 rounded px-2 py-1 text-xs"
	        >
	          <option value="10">10</option>
	          <option value="20">20</option>
	          <option value="30">30</option>
	          <option value="ALL">Show all</option>
	        </select>
	        <span>
	          {pageSize === "ALL"
	            ? `${totalRows} rows`
	            : `${startIndex + 1}–${endIndex} of ${totalRows}`}
	        </span>
	      </div>
	
	      {/* RIGHT / BOTTOM */}
	      {pageSize !== "ALL" && (
	        <div className="flex items-center justify-center gap-2">
	          <button
	            onClick={goPrev}
	            disabled={page <= 1}
	            className="px-3 py-1 rounded bg-gray-800 border border-gray-700 disabled:opacity-40 hover:bg-gray-700"
	          >
	            Prev
	          </button>
	          <span>Page {page} / {totalPages}</span>
	          <button
	            onClick={goNext}
	            disabled={page >= totalPages}
	            className="px-3 py-1 rounded bg-gray-800 border border-gray-700 disabled:opacity-40 hover:bg-gray-700"
	          >
	            Next
	          </button>
	        </div>
	      )}
	    </div>
	  )
	);
  

  // ==========================
  // Effects
  // ==========================

useEffect(() => {
  let timer;
  if (loading) {
    timer = setTimeout(() => setShowLoadingUi(true), 250);
  } else {
    setShowLoadingUi(false);
  }
  return () => clearTimeout(timer);
}, [loading]);

useEffect(() => {
  return () => {
    if (allAbortRef.current) {
      allAbortRef.current.abort();
    }
  };
}, []);

// =====================
// EFFECT A: switching tabs / category
// =====================
useEffect(() => {
  setView("list");

  if (groupBy === "all") {
    if (selectedIndustry) setSelectedIndustry("");
    if (selectedKonglo) setSelectedKonglo("");
    return;
  }

  if (groupBy === "broker") {
    if (selectedIndustry) setSelectedIndustry("");
    if (selectedKonglo) setSelectedKonglo("");
    fetchBrokerFollower();
    return;
  }

  if (groupBy === "industry") {
    if (selectedKonglo) setSelectedKonglo("");
    if (industries.length === 0) fetchIndustries();
    return;
  }

  if (groupBy === "konglo") {
    if (selectedIndustry) setSelectedIndustry("");
    return;
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [category, groupBy]);

// =====================
// EFFECT B: selection & sorting changes (only for industry/konglo)
// =====================
useEffect(() => {
  setView("list");

  if (groupBy === "industry") {
    if (selectedIndustry) {
      fetchStocksByIndustry();
    } else {
      fetchIndustrySummary();
    }
    return;
  }

  if (groupBy === "konglo") {
    if (selectedKonglo) {
      fetchStocksByKonglo();
    } else {
      fetchKongloSummary();
    }
    return;
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [groupBy, selectedIndustry, selectedKonglo, sortKey, sortDir]);

useEffect(() => {
  if (sortKey === "foreign_net") setSortDir("desc");
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [sortKey]);
	

useEffect(() => {
  if (!token) return;
  if (groupBy !== "all") return;

  setView("list");
  fetchAllStocks();
}, [token, groupBy, category]);

useEffect(() => {
  setSelectedIndustry("");
  setSelectedKonglo("");
  setRows([]);
}, [category]);

useEffect(() => {
  if (!token) return;
  fetchWatchlistSet();
}, [token]);

useEffect(() => {
  if (!selectedCode) {
    setIsStarred(false);
    return;
  }
  setIsStarred(watchlistSet.has(selectedCode.toUpperCase().trim()));
}, [selectedCode, watchlistSet]);

useEffect(() => {
  if (!selectedCode) return;
  setView("detail");
}, [selectedCode]);

useEffect(() => {
  if (selectedCode) return;
  setView("list");
}, [selectedCode]);

useEffect(() => {
  const sym =
    (routeCode || selectedCode || "").toUpperCase().trim();

  if (!sym) return;

  setSelectedCode((prev) => {
    if ((prev || "").toUpperCase() === sym) return prev;
    return sym;
  });

  setView("detail");

  fetchDetail(sym, detailYear, detailDays);

  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [routeCode, selectedCode]);

useEffect(() => {
  if (forceSelectedCode) {
    setSelectedCode(forceSelectedCode);
  }
}, [forceSelectedCode]);

useEffect(() => {
  if (!actualSelectedCode) return;
  fetchSummaryRow(actualSelectedCode);
}, [actualSelectedCode, token]);	


// === Build Prompt untuk Ask AI ===
const buildAskAiPrompt = (row) => {
  const lines = [];

  lines.push(`Tolong analisa saham ${row.code} (${row.name || "-"}) di Bursa Efek Indonesia.`);

  lines.push(`\n📌 Snapshot harga & trend:`);
  lines.push(`- Tanggal data: ${row.trade_date || "-"}`);
  lines.push(`- Harga penutupan terakhir: ${row.price ?? "-"}`);
  lines.push(`- Perubahan 4 minggu: ${
    row.chg_4w_pct != null ? row.chg_4w_pct.toFixed(2) + "%" : "-"
  }`);
  lines.push(`- Perubahan 13 minggu: ${
    row.chg_13w_pct != null ? row.chg_13w_pct.toFixed(2) + "%" : "-"
  }`);
  if (row.chg_26w_pct != null || row.chg_52w_pct != null) {
    lines.push(`- Perubahan 26 minggu: ${
      row.chg_26w_pct != null ? row.chg_26w_pct.toFixed(2) + "%" : "-"
    }`);
    lines.push(`- Perubahan 52 minggu: ${
      row.chg_52w_pct != null ? row.chg_52w_pct.toFixed(2) + "%" : "-"
    }`);
  }

  lines.push(`\n📌 Hasil bandarmology internal:`);
  lines.push(`- Tipe saham (size/likuiditas): ${row.stock_type || "-"}`);
  lines.push(`- Sinyal sistem: ${row.signal || "-"}`);
  lines.push(`- Suggest score % : ${
    row.suggest_pct != null ? row.suggest_pct.toFixed(2) + "%" : "-"
  }`);
  lines.push(`- Buy range: ${row.buy_range || "-"}`);
  lines.push(`- TP1 / TP2: ${(row.tp1 ?? "-")} / ${(row.tp2 ?? "-")}`);
  lines.push(`- Risk/Reward (RR): ${row.rr ?? "-"}`);

  lines.push(`\n📌 Snapshot fundamental (dari stock screener):`);
  lines.push(`- Sektor / Subsektor: ${row.sektor || "-"} / ${row.subsektor || "-"}`);
  lines.push(`- Industri / Subindustri: ${row.industri || "-"} / ${row.subindustri || "-"}`);
  lines.push(`- Index: ${row.index_name || "-"}`);
  lines.push(`- Market cap: ${row.mkt_cap ?? "-"} (rupiah)`);
  lines.push(`- PER: ${row.per ?? "-"}`);
  lines.push(`- PBV: ${row.pbv ?? "-"}`);
  lines.push(`- ROE %: ${row.roe_pct ?? "-"}`);
  lines.push(`- ROA %: ${row.roa_pct ?? "-"}`);
  lines.push(`- DER: ${row.der ?? "-"}`);
  lines.push(`- Total revenue (approx): ${row.total_rev ?? "-"}`);

  lines.push(`\nPertanyaan: Apakah saham ini layak untuk swing 1–4 minggu? Jelaskan alasan + rencana & risiko.`);

  return lines.join("\n");
};

const handleAskAi = (row, askType = ASK_AI_TYPES.QUICK_SUMMARY) => {
  if (!onAskAi || !row) return;

  const prompt = buildAskAiPrompt(row, askType, { extractBuyRange });
  const kodeSaham = row.code;

  openAskChat();

  setTimeout(() => {
    onAskAi(prompt, kodeSaham, askType);
  }, 100);
};

  

  const brokerStatusLabel = (status) => {
    if (status === "STRONG_T1") return "Strong T+1";
    if (status === "WATCH_T1") return "Watch T+1";
    if (status === "AVOID_CHASING") return "Avoid Chasing";
    return "Speculative";
  };

  const brokerStatusClass = (status) => {
    if (status === "STRONG_T1") return "bg-emerald-500/15 text-emerald-300 border-emerald-500/30";
    if (status === "WATCH_T1") return "bg-blue-500/15 text-blue-300 border-blue-500/30";
    if (status === "AVOID_CHASING") return "bg-red-500/15 text-red-300 border-red-500/30";
    return "bg-amber-500/15 text-amber-300 border-amber-500/30";
  };

  const BrokerFollowerTable = () => (
    <div className="overflow-x-auto rounded-lg border border-gray-700 bg-gray-900">
      <div className="px-3 py-3 border-b border-gray-700">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
          <div>
            <div className="text-sm font-semibold text-gray-100">Broker Follower</div>
            <div className="text-xs text-gray-400">
              Kandidat T+1 dari akumulasi broker BK, AK, RX, KZ, MG, ZP. Target TP 5%, tidak untuk disimpan.
            </div>
          </div>
			
			<div className="flex flex-wrap items-end gap-2">
			  <div>
			    <label className="block text-xs font-medium mb-1 text-slate-300">
			      Trade Date
			    </label>
			    <input
			      type="date"
			      value={brokerDate || ""}
			      onChange={(e) => setBrokerDate(e.target.value)}
			      className="w-full min-w-[150px] rounded-md bg-slate-900 border border-slate-700 px-2 py-2 text-sm text-slate-100 outline-none [color-scheme:dark] focus:border-blue-500"
			    />
			  </div>
			
			  <button
			    onClick={() => fetchBrokerFollower(brokerDate)}
			    disabled={brokerLoading}
			    className="h-[38px] rounded-md bg-slate-700 px-3 text-xs font-semibold text-slate-100 hover:bg-slate-600 disabled:cursor-not-allowed disabled:opacity-50"
			  >
			    {brokerLoading ? "Loading..." : "Load"}
			  </button>
			
			  <button
			    onClick={() => {
			      setBrokerDate("");
			      fetchBrokerFollower("");
			    }}
			    disabled={brokerLoading}
			    className="h-[38px] rounded-md bg-slate-700 px-3 text-xs font-semibold text-slate-100 hover:bg-slate-600 disabled:cursor-not-allowed disabled:opacity-50"
			  >
			    Latest
			  </button>
			</div>
			
        </div>
      </div>

      {brokerLoading ? (
        <div className="px-3 py-6 text-sm text-gray-400">Memuat Broker Follower...</div>
      ) : brokerError ? (
        <div className="px-3 py-6 text-sm text-red-300">{brokerError}</div>
      ) : pagedRows.length === 0 ? (
        <div className="px-3 py-6 text-sm text-gray-400">Belum ada kandidat Broker Follower.</div>
      ) : (
        <>
          <table className="hidden md:table min-w-full text-xs">
            <thead className="sticky top-0 bg-gray-800 text-gray-200 z-10">
              <tr>
                <th className="px-3 py-2 text-left">Date</th>
                <th className="px-3 py-2 text-left">Saham</th>
                <th className="px-3 py-2 text-left">Status</th>
                <th className="px-3 py-2 text-right">TP 5%</th>
                <th className="px-3 py-2 text-right">Case</th>
                <th className="px-3 py-2 text-right">Return T</th>
				<th className="px-3 py-2 text-right">Return 3D</th>
				<th className="px-3 py-2 text-right">Return 5D</th>
                <th className="px-3 py-2 text-right">Close Pos</th>
                <th className="px-3 py-2 text-right">Cluster Net</th>
				<th className="px-3 py-2 text-right">Cluster %</th>
                <th className="px-3 py-2 text-center">Brokers</th>
                <th className="px-3 py-2 text-right">Best Rank</th>
              </tr>
            </thead>
            <tbody>
              {pagedRows.map((row, idx) => (
                <tr
                  key={`${row.kode_saham || row.symbol || idx}-${row.trade_date || idx}`}
                  className="border-b border-gray-700 odd:bg-gray-800 even:bg-gray-900 hover:bg-gray-700 transition"
                >
                  <td className="px-3 py-2 whitespace-nowrap text-gray-300">{row.trade_date || "-"}</td>
                  <td className="px-3 py-2">
                    <button
                      onClick={() => handleClickCode(row.kode_saham || row.symbol || row.code)}
                      className="text-blue-300 hover:underline font-semibold"
                    >
                      {row.kode_saham || row.symbol || row.code || "-"}
                    </button>
                  </td>
                  <td className="px-3 py-2">
                    <span className={`inline-flex px-2 py-1 rounded-full border text-[11px] font-semibold ${brokerStatusClass(row.broker_follower_status)}`}>
                      {brokerStatusLabel(row.broker_follower_status)}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right">{fmtPct(row.tp_5_hit_ratio_pct)}</td>
                  <td className="px-3 py-2 text-right">{fmtInt(row.total_case)}</td>
				  <td className={`px-3 py-2 text-right ${getPctClass(row.return_today_pct)}`}>
				    {fmtPct(row.return_today_pct)}
				  </td>
				  <td className={`px-3 py-2 text-right ${getPctClass(row.return_3d)}`}>
				    {row.return_3d == null ? "Null" : fmtPct(row.return_3d)}
				  </td>
				  <td className={`px-3 py-2 text-right ${getPctClass(row.return_5d)}`}>
				    {row.return_5d == null ? "Null" : fmtPct(row.return_5d)}
				  </td>
				  <td className="px-3 py-2 text-right">{fmtPct(row.close_position_pct)}</td>
                  <td className="px-3 py-2 text-right">{fmtInt(row.total_net_value_cluster)}</td>
				  <td className="px-3 py-2 text-right">
				    {row.cluster_net_to_value_pct == null ? "Null" : fmtPct(row.cluster_net_to_value_pct)}
				  </td>				
                  <td className="px-3 py-2 text-center text-gray-300">{row.broker_net_buy_list || "-"}</td>
                  <td className="px-3 py-2 text-right">{row.best_target_broker_rank ?? "-"}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="md:hidden divide-y divide-gray-700">
            {pagedRows.map((row, idx) => (
              <div
                key={`${row.kode_saham || row.symbol || idx}-${row.trade_date || idx}-mobile`}
                className="p-3 bg-gray-900"
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <button
                      onClick={() => handleClickCode(row.kode_saham || row.symbol || row.code)}
                      className="text-blue-300 hover:underline font-semibold text-base"
                    >
                      {row.kode_saham || row.symbol || row.code || "-"}
                    </button>
                    <div className="text-[11px] text-gray-500">{row.trade_date || "-"}</div>
                  </div>
                  <span className={`inline-flex px-2 py-1 rounded-full border text-[11px] font-semibold ${brokerStatusClass(row.broker_follower_status)}`}>
                    {brokerStatusLabel(row.broker_follower_status)}
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-2 mt-3 text-xs">
                  <div className="rounded bg-gray-800 p-2">
                    <div className="text-gray-500">TP 5% Hit</div>
                    <div className="text-gray-100 font-semibold">{fmtPct(row.tp_5_hit_ratio_pct)}</div>
                  </div>
                  <div className="rounded bg-gray-800 p-2">
                    <div className="text-gray-500">Case</div>
                    <div className="text-gray-100 font-semibold">{fmtInt(row.total_case)}</div>
                  </div>
                  <div className="rounded bg-gray-800 p-2">
                    <div className="text-gray-500">Return T</div>
                    <div className={`font-semibold ${getPctClass(row.return_today_pct)}`}>{fmtPct(row.return_today_pct)}</div>
                  </div>
                  <div className="rounded bg-gray-800 p-2">
                    <div className="text-gray-500">Cluster Net</div>
                    <div className="text-gray-100 font-semibold">{fmtInt(row.total_net_value_cluster)}</div>
                  </div>
                </div>

                <div className="mt-2 text-xs text-gray-400">
                  Broker: <span className="text-gray-200">{row.broker_net_buy_list || "-"}</span>
                  <span className="mx-2">•</span>
                  Best Rank: <span className="text-gray-200">{row.best_target_broker_rank ?? "-"}</span>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );

  // ==========================
  // Render
  // ==========================
  return (
    <>
      <div className="h-full flex flex-col">
        {/* Header controls – responsive */}
		    {!selectedCode && (
          <div className="p-3 border-b bg-gray-800">
              
            {/* ================= FILTER (MOBILE) ================= */}
            <div className="block md:hidden space-y-2">

              {/* Row 1: Tabs (full width) */}
              <div className="w-full">
                <div className="grid grid-cols-4 rounded border border-gray-700 bg-gray-900 p-1 h-9">
                  {[
                    { value: "all", label: "All Stocks" },
                    { value: "broker", label: "Broker" },
                    { value: "konglo", label: "Konglo" },
                    { value: "industry", label: "Industri" },
                  ].map((t) => (
                    <button
                      key={t.value}
                      type="button"
                      onClick={() => {
                        setGroupBy(t.value);
                        if (t.value !== "all" && t.value !== "broker") setSearch("");
                      }}                
                      className={`h-7 rounded text-[12px] font-semibold transition
                        ${groupBy === t.value ? "bg-gray-700 text-gray-100" : "text-gray-400 hover:text-gray-200"}`}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Row 2: Conditional filters (2 cols) */}
              {groupBy === "konglo" || groupBy === "broker" ? null : groupBy === "industry" ? (
                <div className="grid grid-cols-2 gap-2">
                  <select
                    className="h-9 w-full border border-gray-600 bg-gray-900 text-gray-200 rounded px-2 text-[12px]"
                    value={sortKey}
                    onChange={(e) => setSortKey(e.target.value)}
                  >
                    <option value="foreign_net">F Nett</option>
                    <option value="chg_4w_pct">Avg 4W %</option>
                    <option value="code">Industri (A-Z)</option>
                  </select>

                  <select
                    className="h-9 w-full border border-gray-600 bg-gray-900 text-gray-200 rounded px-2 text-[12px]"
                    value={sortDir}
                    onChange={(e) => setSortDir(e.target.value)}
                  >
                    <option value="desc">Desc</option>
                    <option value="asc">Asc</option>
                  </select>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-2">
                  <select
                    className="h-9 w-full border border-gray-600 bg-gray-900 text-gray-200 rounded px-2 text-[12px]"
                    value={signalFilter}
                    onChange={(e) => setSignalFilter(e.target.value)}
                  >
                    <option value="ALL">All</option>
                    <option value="BUY">BUY</option>
                    <option value="WATCH">WATCH</option>
                    <option value="NEUTRAL">NEUTRAL</option>
                    <option value="AVOID">AVOID</option>
                  </select>

                  <select
                    className="h-9 w-full border border-gray-600 bg-gray-900 text-gray-200 rounded px-2 text-[12px]"
                    value={stockTypeFilter}
                    onChange={(e) => setStockTypeFilter(e.target.value)}
                  >
                    <option value="ALL">All</option>
                    <option value="BLUECHIP">Bluechip</option>
                    <option value="MEDIUM">Medium</option>
                    <option value="SMALL">Small</option>
                    <option value="GORENGAN">Gorengan</option>
                  </select>
                </div>
              )}

              {/* Row 3: Search untuk All Stocks dan Broker */}
              {(groupBy === "all" || groupBy === "broker") ? (
                <div className="relative">
                  <input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search kode saham"
                    className="h-9 w-full border border-gray-600 bg-gray-900 text-gray-200 rounded px-2 text-[12px] pr-10"
                  />
                  {search ? (
                    <button
                      type="button"
                      onClick={() => setSearch("")}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-200"
                      title="Clear"
                    >
                      ✕
                    </button>
                  ) : null}
                </div>
              ) : null}

            </div>
    

            {/* DESKTOP FILTER */}
            <div className="hidden md:block w-full">
              <div className="flex flex-col gap-2">

                {/* Row 1: Tabs only */}
                <div className="grid grid-cols-12 gap-2 items-center">
                  <div className="col-span-5 min-w-0">
                    <div className="grid grid-cols-4 rounded border border-gray-700 bg-gray-900 p-1 h-9 w-full">
                      {[
                        { value: "all", label: "All Stocks" },
                        { value: "broker", label: "Broker" },
                        { value: "konglo", label: "Konglo" },
                        { value: "industry", label: "Industri" },
                      ].map((t) => (
                        <button
                          key={t.value}
                          type="button"
                          onClick={() => {
                            setGroupBy(t.value);
                            if (t.value !== "all" && t.value !== "broker") setSearch("");
                          }}                    
                          className={`h-7 rounded text-sm font-semibold transition whitespace-nowrap
                            ${groupBy === t.value
                              ? "bg-gray-700 text-gray-100"
                              : "text-gray-400 hover:text-gray-200"
                            }`}
                        >
                          {t.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="col-span-7" />
                </div>

                {/* Row 2: 3 columns (rightmost = Search only for All Stocks) */}
                {groupBy === "konglo" || groupBy === "broker" ? (
                  <div className="grid grid-cols-12 gap-2 items-center">
                    <div className="col-span-12 text-sm text-gray-400" />
                  </div>
                ) : groupBy === "industry" ? (
                  <div className="grid grid-cols-12 gap-2 items-center">
                    {/* Col 1 */}
                    <div className="col-span-3 min-w-0">
                      <select
                        className="h-9 w-full border border-gray-600 bg-gray-900 text-gray-200 rounded px-2 text-sm"
                        value={sortKey}
                        onChange={(e) => setSortKey(e.target.value)}
                      >
                        <option value="foreign_net">F Nett</option>
                        <option value="total_value">Total Value</option>
                        <option value="chg_4w_pct">Avg 4W %</option>
                        <option value="chg_13w_pct">Avg 13W %</option>
                        <option value="code">Industri (A-Z)</option>
                      </select>
                    </div>

                    {/* Col 2 */}
                    <div className="col-span-2 min-w-0">
                      <select
                        className="h-9 w-full border border-gray-600 bg-gray-900 text-gray-200 rounded px-2 text-sm"
                        value={sortDir}
                        onChange={(e) => setSortDir(e.target.value)}
                      >
                        <option value="desc">Desc</option>
                        <option value="asc">Asc</option>
                      </select>
                    </div>

                    {/* Col 3 (right): NO search for industry -> spacer */}
                    <div className="col-span-7" />
                  </div>
                ) : (
                  // ✅ All Stocks
                  <div className="grid grid-cols-12 gap-2 items-center">
                    {/* Col 1 */}
                    <div className="col-span-3 min-w-0">
                      <select
                        className="h-9 w-full border border-gray-600 bg-gray-900 text-gray-200 rounded px-2 text-sm"
                        value={signalFilter}
                        onChange={(e) => setSignalFilter(e.target.value)}
                      >
                        <option value="ALL">All</option>
                        <option value="BUY">BUY</option>
                        <option value="WATCH">WATCH</option>
                        <option value="NEUTRAL">NEUTRAL</option>
                        <option value="AVOID">AVOID</option>
                      </select>
                    </div>

                    {/* Col 2 */}
                    <div className="col-span-3 min-w-0">
                      <select
                        className="h-9 w-full border border-gray-600 bg-gray-900 text-gray-200 rounded px-2 text-sm"
                        value={stockTypeFilter}
                        onChange={(e) => setStockTypeFilter(e.target.value)}
                      >
                        <option value="ALL">All</option>
                        <option value="BLUECHIP">Bluechip</option>
                        <option value="MEDIUM">Medium</option>
                        <option value="SMALL">Small</option>
                        <option value="GORENGAN">Gorengan</option>
                      </select>
                    </div>

                    {/* Col 3 (right): Search ONLY for All Stocks */}
                    <div className="col-span-6 min-w-0 relative">
                      <input
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search kode saham"
                        className="h-9 w-full border border-gray-600 bg-gray-900 text-gray-200 rounded px-2 text-sm pr-10"
                      />
                      {search ? (
                        <button
                          type="button"
                          onClick={() => setSearch("")}
                          className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-200"
                          title="Clear"
                        >
                          ✕
                        </button>
                      ) : null}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
		    )}

			
        {/* Content */}
        <div className="p-3 overflow-auto">

          {/* Breadcrumb untuk subindustri & konglo */}
          {groupBy === "industry" && selectedIndustry && (
            <div className="flex items-center justify-between mb-2">
              {/* Breadcrumb */}
              <div className="text-xs text-gray-300">
                <button
                  onClick={() => setSelectedIndustry("")}
                  className="text-blue-300 hover:underline"
                >
                  Subindustri
                </button>
                <span className="mx-1">{">"}</span>
                <span className="font-semibold text-gray-100">
                  {selectedIndustry}
                </span>
              </div>
          
              {/* Tombol back eksplisit (opsional, bisa di-skip kalau cukup breadcrumb) */}
              <button
                onClick={() => setSelectedIndustry("")}
                className="px-2 py-1 rounded bg-gray-700 hover:bg-gray-600 text-xs text-gray-100"
              >
                ← Kembali ke daftar Industri
              </button>
            </div>
          )}

          {groupBy === "konglo" && selectedKonglo && (
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs text-gray-300">
                <button
                  onClick={() => setSelectedKonglo("")}
                  className="text-blue-300 hover:underline"
                >
                  Konglo
                </button>
                <span className="mx-1">{">"}</span>
                <span className="font-semibold text-gray-100">
                  {selectedKonglo}
                </span>
              </div>
          
              <button
                onClick={() => setSelectedKonglo("")}
                className="px-2 py-1 rounded bg-gray-700 hover:bg-gray-600 text-xs text-gray-100"
              >
                ← Kembali ke daftar Konglo
              </button>
            </div>
          )}
          
          {view === "list" ? (
            <>
              {showLoadingUi ? (
                <StocksLoadingSkeleton />            
              ) : category === "watchlist" ? (
                // === TABLE WATCHLIST ===
                  <table className="min-w-full text-xs border border-gray-700 rounded-lg overflow-hidden">
                    <thead>
                      <tr className="bg-gray-700 text-gray-200">
                        <th className="py-2 pr-3 text-left">Code</th>
                        <th className="py-2 pr-3 text-left">Name</th>
                        <th className="py-2 pr-3 text-right">%Chg</th>
                        <th className="py-2 pr-3 text-right">Foreign Net</th>
                        <th className="py-2 pr-3 text-right">Monitor</th>
                        <th className="py-2 pr-3 text-right">Freq</th>
                        <th className="py-2 pr-3 text-right">Value</th>
                        <th className="py-2 pr-3 text-right">WAP</th>
                        <th className="py-2 pr-3 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {processedRows.map((r, idx) => (
                        <tr
                          key={rowKey(r, idx)}
                          className="border-b border-gray-700 odd:bg-gray-800 even:bg-gray-900"
                        >
                          <td className="px-3 py-2">
                            <button
                              onClick={() =>
                                handleClickCode(r.symbol || r.code)
                              }
                              className="text-blue-400 hover:underline font-semibold"
                            >
                              {r.symbol || r.code}
                            </button>
                          </td>
                          <td className="py-1 pr-3 text-gray-200">
                            {r.name ?? "-"}
                          </td>
                          <td className="py-1 pr-3 text-right">
                            <span
                              className={
                                (r.pct_chg ?? r.pct ?? 0) < 0
                                  ? "text-red-400"
                                  : (r.pct_chg ?? r.pct ?? 0) > 0
                                  ? "text-emerald-400"
                                  : "text-gray-300"
                              }
                            >
                              {r.pct_chg ?? r.pct ?? "-"}
                            </span>
                          </td>
                          <td className="py-1 pr-3 text-right">
                            {r.foreign_net?.toLocaleString("id-ID") ?? "-"}
                          </td>
                          <td className="py-1 pr-3 text-right">
                            {watchlistSet.has(
                              (r.symbol || r.code || "").toUpperCase()
                            ) ? (
                              <span
                                className="text-blue-300 flex items-center justify-center"
                                title="Monitored in Watchlist"
                              >
                                <EyeIcon />
                              </span>
                            ) : (
                              ""
                            )}
                          </td>
                          <td className="py-1 pr-3 text-right">
                            {r.freq?.toLocaleString("id-ID") ?? "-"}
                          </td>
                          <td className="py-1 pr-3 text-right">
                            {r.value?.toLocaleString("id-ID") ?? "-"}
                          </td>
                          <td className="py-1 pr-3 text-right">
                            {r.wap ?? "-"}
                          </td>
                          <td className="py-1 pr-3 text-right">
                            <button
                              onClick={() => handleRemove(r.symbol || r.code)}
                              className="px-2 py-1 rounded bg-red-600/80 hover:bg-red-600 text-white font-semibold"
                              title="Remove from Watchlist"
                            >
                              Del
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>                  </table>

                ) : groupBy === "broker" ? (
                  <BrokerFollowerTable />

                ) : groupBy === "industry" && !selectedIndustry ? (
                <div className="overflow-x-auto rounded-lg border border-gray-700">
        
                  {/* 1. TAMPILAN DESKTOP (hidden di mobile, muncul di md) */}
                  <table className="hidden md:table min-w-full text-xs">
                    <thead className="sticky top-0 bg-gray-700 text-gray-200 z-10">
                      <tr className="bg-gray-800">
                        <th className="px-2 py-2 text-left w-1/4">Industri</th>
                        <th className="px-2 py-2 text-right">#BUY</th>
                        <th className="px-2 py-2 text-right">#WATCH</th>
                        <th className="px-2 py-2 text-right">#NEUTRAL</th>
                        <th className="px-2 py-2 text-right">#AVOID</th>
                        <th className="px-2 py-2 text-right">Avg 4W %</th>
                        <th className="px-2 py-2 text-right">Avg 13W %</th>
                        <th className="px-2 py-2 text-right">Total Value</th>
                        <th className="px-2 py-2 text-right">F Buy</th>
                        <th className="px-2 py-2 text-right">F Sell</th>
                        <th className="px-2 py-2 text-right">F Nett</th>
                      </tr>
                    </thead>
              
                    <tbody>
                      {industrySummaryRows.map((row, idx) => (
                        <tr
                          key={row.industri || idx}
                          className="border-b border-gray-700 odd:bg-gray-800 even:bg-gray-900 hover:bg-gray-700 cursor-pointer transition"
                          onClick={() => setSelectedIndustry(row.industri)}
                        >
                          <td className="px-3 py-2 text-blue-300 font-semibold">
                            {row.industri}
                          </td>
                          <td className="px-2 py-1 text-right">{fmtInt(row.count_buy)}</td>
                          <td className="px-2 py-1 text-right">{fmtInt(row.count_watch)}</td>
                          <td className="px-2 py-1 text-right">{fmtInt(row.count_neutral)}</td>
                          <td className="px-2 py-1 text-right">{fmtInt(row.count_avoid)}</td>
              
                          <td className={`px-2 py-1 text-right ${getPctClass(row.avg_4w_pct)}`}>
                            {row.avg_4w_pct != null ? fmtPct(row.avg_4w_pct) : "-"}
                          </td>
                          <td className={`px-2 py-1 text-right ${getPctClass(row.avg_13w_pct)}`}>
                            {row.avg_13w_pct != null ? fmtPct(row.avg_13w_pct) : "-"}
                          </td>
              
                          <td className="px-2 py-1 text-right">{fmtInt(row.total_value)}</td>
                          <td className="px-2 py-1 text-right">{fmtInt(row.total_fbuy)}</td>
                          <td className="px-2 py-1 text-right">{fmtInt(row.total_fsell)}</td>
              
                          <td className={`px-2 py-1 text-right ${getPctClass(row.foreign_net)}`}>
                            {fmtInt(row.foreign_net)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
        
                  {/* 2. TAMPILAN MOBILE (muncul di mobile, hidden di md) */}
                  <div className="md:hidden">
                    <table className="min-w-full table-auto text-xs">
                      <thead className="sticky top-0 bg-gray-700 text-gray-200 z-10">
                        <tr className="bg-gray-800">
                          <th className="py-2 pr-2 pl-3 text-left w-2/5">Industri</th>
                          <th className="py-2 pr-2 text-right w-1/12">#BUY</th>
                          <th className="py-2 pr-2 text-right w-1/4">4W %</th>
                          <th className="py-2 pr-3 text-right w-1/4">F Nett</th>
                        </tr>
                      </thead>
              
                      <tbody>
                        {industrySummaryRows.map((row, idx) => (
                          <tr
                            key={row.industri || idx}
                            className="border-b border-gray-700 odd:bg-gray-800 even:bg-gray-900 hover:bg-gray-700 cursor-pointer transition"
                            onClick={() => setSelectedIndustry(row.industri)}
                          >
                            <td className="px-3 py-2 text-blue-300 font-semibold truncate max-w-[120px]">
                              {row.industri}
                            </td>
                            <td className="py-2 pr-2 text-right">{fmtInt(row.count_buy)}</td>
                            <td className={`py-2 pr-2 text-right ${getPctClass(row.avg_4w_pct)}`}>
                              {row.avg_4w_pct != null ? fmtPct(row.avg_4w_pct) : "-"}
                            </td>
                            <td className={`py-2 pr-3 text-right ${getPctClass(row.foreign_net)}`}>
                              {fmtInt(row.foreign_net)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                ) : groupBy === "konglo" && !selectedKonglo ? (
                
                  <div className="overflow-x-auto rounded-lg border border-gray-700"> 
                    {/* 1. TAMPILAN DESKTOP (hidden di mobile, muncul di md) */}
                    <table className="hidden md:block w-full text-xs"> 
                      <thead className="sticky top-0 bg-gray-700 text-gray-200 z-10">
                          <tr className="bg-gray-800">
                              {/* Menyesuaikan lebar kolom untuk memicu lebar penuh */}
                              <th className="px-2 py-2 text-left w-2/12">Konglo</th>
                              <th className="px-2 py-2 text-right w-[5%]">#BUY</th>
                              <th className="px-2 py-2 text-right w-[5%]">#WATCH</th>
                              <th className="px-2 py-2 text-right w-[6%]">#NEUTRAL</th>
                              <th className="px-2 py-2 text-right w-[6%]">#AVOID</th>
                              <th className="px-2 py-2 text-right w-[8%]">Avg 4W %</th>
                              <th className="px-2 py-2 text-right w-[8%]">Avg 13W %</th>
                              <th className="px-2 py-2 text-right w-[15%]">Total Value</th>
                              <th className="px-2 py-2 text-right w-[12%]">F Buy</th>
                              <th className="px-2 py-2 text-right w-[12%]">F Sell</th>
                              <th className="px-2 py-2 text-right w-[12%]">F Nett</th>
                          </tr>
                      </thead>
                      <tbody>
                        {kongloSummaryRows.map((row, idx) => (
                            <tr
                                key={row.konglo || idx}
                                className="border-b border-gray-700 odd:bg-gray-800 even:bg-gray-900 hover:bg-gray-700 cursor-pointer transition"
                                onClick={() => setSelectedKonglo(row.konglo)}
                            >
                                <td className="px-3 py-2 text-blue-300 font-semibold">{row.konglo}</td>
                                <td className="px-2 py-1 text-right">{fmtInt(row.count_buy)}</td>
                                <td className="px-2 py-1 text-right">{fmtInt(row.count_watch)}</td>
                                <td className="px-2 py-1 text-right">{fmtInt(row.count_neutral)}</td>
                                <td className="px-2 py-1 text-right">{fmtInt(row.count_avoid)}</td>
                                <td className={`px-2 py-1 text-right ${getPctClass(row.avg_4w_pct)}`}>
                                    {row.avg_4w_pct != null ? fmtPct(row.avg_4w_pct) : "-"}
                                </td>
                                <td className={`px-2 py-1 text-right ${getPctClass(row.avg_13w_pct)}`}>
                                    {row.avg_13w_pct != null ? fmtPct(row.avg_13w_pct) : "-"}
                                </td>
                                <td className="px-2 py-1 text-right">{fmtInt(row.total_value)}</td>
                                <td className="px-2 py-1 text-right">{fmtInt(row.total_fbuy)}</td>
                                <td className="px-2 py-1 text-right">{fmtInt(row.total_fsell)}</td>
                                <td className={
                                    `px-2 py-1 text-right ${getPctClass(row.foreign_net)}`
                                }>
                                    {fmtInt(row.foreign_net)}
                                </td>
                            </tr>
                        ))}
                      </tbody>
                    </table>
      
                    {/* 2. TAMPILAN MOBILE KONGLO (muncul di mobile, hidden di md) */}
                    <div className="md:hidden">
                      <table className="min-w-full table-auto text-xs">
                          <thead className="sticky top-0 bg-gray-700 text-gray-200 z-10">
                              <tr className="bg-gray-800">
                                  <th className="py-2 pr-2 pl-3 text-left w-1/4">Konglo</th>
                                  <th className="py-2 pr-2 text-right w-[15%]">BUY</th>
                                  <th className="py-2 pr-2 text-right w-[15%]">WATCH</th>
                                  <th className="py-2 pr-2 text-right w-[20%]">4W %</th>
                                  {/* Kolom 13W % di-hide untuk mobile view */}
                                  {/* <th className="py-2 pr-2 text-right w-[15%]">13W %</th> */}
                                  <th className="py-2 pr-3 text-right w-1/4">F Nett</th> 
                              </tr>
                          </thead>
                          <tbody>
                              {kongloSummaryRows.map((row, idx) => (
                                  <tr
                                      key={row.konglo || idx}
                                      className="border-b border-gray-700 odd:bg-gray-800 even:bg-gray-900 hover:bg-gray-700 cursor-pointer transition"
                                      onClick={() => setSelectedKonglo(row.konglo)}
                                  >
                                      <td className="px-3 py-2 text-blue-300 font-semibold truncate max-w-[100px]">{row.konglo}</td>
                                      <td className="py-2 pr-2 text-right">{fmtInt(row.count_buy)}</td>
                                      <td className="py-2 pr-2 text-right">{fmtInt(row.count_watch)}</td>
                                      <td className={`py-2 pr-2 text-right ${getPctClass(row.avg_4w_pct)}`}>
                                          {row.avg_4w_pct != null ? fmtPct(row.avg_4w_pct) : "-"}
                                      </td>
                                      {/* Kolom 13W % di-hide untuk mobile view */}
                                      {/* <td className={`py-2 pr-2 text-right ${getPctClass(row.avg_13w_pct)}`}>
                                          {row.avg_13w_pct != null ? fmtPct(row.avg_13w_pct) : "-"}
                                      </td> */}
                                      <td className={
                                          `py-2 pr-3 text-right ${getPctClass(row.foreign_net)}`
                                      }>
                                          {fmtInt(row.foreign_net)}
                                      </td>
                                  </tr>
                              ))}
                          </tbody>
                      </table>
                    </div>
                  </div>
                
                ) : (

                  // ===== ALL STOCKS TABLE + SUBINDUSTRI =====
                  <>
                    {/* ========== DESKTOP: TABLE (md ke atas) ========== */}
                    <div className="hidden md:block">
                      <table className="min-w-full text-xs border border-gray-700 rounded-lg overflow-hidden">
                        <thead>
                          <tr className="bg-gray-800">
                            <th className="px-2 py-2 text-left">Code</th>
                            <th className="px-2 py-2 text-left">Last</th>
                            <th className="px-2 py-2 text-left">Time</th>
                            <th className="px-2 py-2 text-right">Close</th>
                            <th className="px-2 py-2 text-left">Chg %</th>
                            <th className="px-2 py-2 text-right">4W %</th>
                            <th className="px-2 py-2 text-right">13W %</th>
                            <th className="px-2 py-2 text-right">Confidence</th>
                            <th className="px-2 py-2 text-left">Signal</th>
                            <th className="px-2 py-2 text-left">Timing</th>                          
                            <th className="px-2 py-2 text-left">Buy Range</th>
                            <th className="px-2 py-2 text-right">TP1 / TP2</th>
                            <th className="px-2 py-2 text-right">R/R</th>
                            <th className="px-2 py-2 text-right">Actions</th>
                          </tr>
                        </thead>
                    
                        <tbody>
                          {pagedRows.map((row, idx) => {
                            const { label: timingLabel, className: timingClass } = getTimingStatus(row);
                            const [buyLow, buyHigh] = extractBuyRange(row);

                            const last = Number(row.last_price);
                            const close = Number(row.price);

                            const fmtConfidence = (suggestPct) => {
                              if (suggestPct == null || isNaN(Number(suggestPct))) return "-";

                              const confidence = Math.round((Number(suggestPct) / 20) * 90);

                              return `${Math.max(0, Math.min(confidence, 90))}%`;
                            };

                            let changePct = null;
                            if (close && last) {
                              changePct = ((last - close) / close) * 100;
                            }
                        
                            return (
                              <tr
                                key={rowKey(row, idx)}
                                className={`border-b border-gray-700 odd:bg-gray-800 even:bg-gray-900
                                            ${row.signal === "BUY" ? "bg-emerald-900/25" : ""}`}
                              >
                                <td className="px-3 py-2">
                                  <button
                                    onClick={() => handleClickCode(row.code)}
                                    className="text-blue-400 hover:underline font-semibold"
                                    title="Lihat detail 30 hari"
                                  >
                                    {row.code}
                                  </button>
                                  {row.stock_type && (
                                    <span
                                      className={`
                                        ml-2 px-2 py-[1px] rounded-full text-[10px] font-semibold
                                        ${
                                          row.stock_type === "BLUECHIP"
                                            ? "bg-blue-500/20 text-blue-300"
                                            : row.stock_type === "MEDIUM"
                                            ? "bg-orange-500/20 text-orange-300"
                                            : row.stock_type === "GORENGAN"
                                            ? "bg-red-500/20 text-red-300"
                                            : "bg-yellow-500/10 text-yellow-200"
                                        }
                                      `}
                                    >
                                      {row.stock_type === "BLUECHIP"
                                        ? "BCHIP"
                                        : row.stock_type === "MEDIUM"
                                        ? "MEDIUM"
                                        : row.stock_type === "GORENGAN"
                                        ? "GORENG"
                                        : row.stock_type}
                                    </span>                                  
                                  )}
                                </td>
                        
                                <td className="text-right">{row.last_price ?? "-"}</td>

                                <td className="text-center">
                                  {row.last_time ? row.last_time.slice(0, 5) : "-"}
                                </td>
                        
                                <td className="px-2 py-1 text-right">
                                  {row.price != null ? fmtNum(row.price) : "-"}
                                </td>

                                <td className="px-2 py-1 text-right">
                                  {changePct === null ? (
                                    <span className="text-gray-400">—</span>
                                  ) : (
                                    <span
                                      className={
                                        changePct > 0
                                          ? "text-green-400"
                                          : changePct < 0
                                          ? "text-red-400"
                                          : "text-gray-200"
                                      }
                                    >
                                      {changePct > 0 ? "+" : ""}
                                      {changePct.toFixed(2)}%
                                    </span>
                                  )}
                                </td>
                        
                                <td className={`px-2 py-1 text-right ${getPctClass(row.chg_4w_pct)}`}>
                                  {row.chg_4w_pct != null ? fmtPct(row.chg_4w_pct) : "-"}
                                </td>
                                <td className={`px-2 py-1 text-right ${getPctClass(row.chg_13w_pct)}`}>
                                  {row.chg_13w_pct != null ? fmtPct(row.chg_13w_pct) : "-"}
                                </td>

                                <td
                                  className="px-2 py-1 text-right"
                                  title="Confidence Level adalah tingkat keyakinan sistem, bukan jaminan hasil."
                                >
                                  {fmtConfidence(row.suggest_pct)}
                                </td>
                        
                                <td className="px-2 py-1">{row.signal ?? "-"}</td>
                        
                                <td className={`text-center text-xs ${timingClass}`}>
                                  {timingLabel}
                                </td>
                        
                                <td className="px-2 py-1">
                                  {buyLow != null && buyHigh != null
                                    ? `${fmtFloor(buyLow)} - ${fmtFloor(buyHigh)}`
                                    : "-"}
                                </td>

                                <td className="px-2 py-1 text-right">
                                  {row.tp1 || row.tp2
                                    ? `${fmtFloor(row.tp1)} / ${fmtFloor(row.tp2)}`
                                    : "-"}
                                </td>

                                <td className="px-2 py-1 text-right">
                                  {row.rr != null ? row.rr : "-"}
                                </td>
                        
                                <td className="py-1 pr-3 text-right space-x-2">
									<button
									  onClick={() => handleAskAi(row, ASK_AI_TYPES.FULL_ANALYSIS)}
									  className="px-2 py-1 rounded bg-indigo-600 hover:bg-indigo-500 text-white font-semibold text-xs"
									  title="Analisa lengkap saham (AI)"
									>
									  Ask AI
									</button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    <PaginationBar className="hidden md:flex" />
                    </div>
                    
                    {/* ========== MOBILE: CARDS (di bawah md) ========== */}
                    {/* ===== MOBILE WRAPPER (cards + pagination) ===== */}
                    <div className="md:hidden pb-28">
                      
                      {/* ========== MOBILE: CARDS (di bawah md) ========== */}
                      <div className="space-y-2">
                        {pagedRows.map((row, idx) => (
                          <div
                          key={rowKey(row, idx)}
                          className={`mb-3 rounded-xl border border-gray-700 p-3 shadow-sm
                                ${row.signal === "BUY" ? "bg-emerald-800/15" : "bg-gray-800"}`}
                          >
                          <div className="flex items-center justify-between mb-1">
                            <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleClickCode(row.code)}
                              className="text-blue-300 font-semibold text-sm"
                              title="Lihat detail 30 hari"
                            >
                              {row.code}
                            </button>
                            {row.stock_type && (
                              <span
                                className={`
                                  ml-2 px-2 py-[1px] rounded-full text-[10px] font-semibold
                                  ${
                                    row.stock_type === "BLUECHIP"
                                      ? "bg-blue-500/20 text-blue-300"
                                      : row.stock_type === "MEDIUM"
                                      ? "bg-orange-500/20 text-orange-300"
                                      : row.stock_type === "GORENGAN"
                                      ? "bg-red-500/20 text-red-300"
                                      : "bg-yellow-500/10 text-yellow-200"
                                  }
                                `}
                              >
                                {row.stock_type === "BLUECHIP"
                                  ? "BCHIP"
                                  : row.stock_type === "MEDIUM"
                                  ? "MEDIUM"
                                  : row.stock_type === "GORENGAN"
                                  ? "GORENG"
                                  : row.stock_type}
                              </span>
                            )}
                            </div>
                            <span className="text-[10px] text-gray-400">
                            {row.trade_date}
                            </span>
                          </div>
                        
                          <div className="flex justify-between text-xs mt-1">
                            <div className="space-y-1">
                            
                            {/* LAST: harga + tanggal + jam */}
                            <div>
                              Last:{" "}
                              <span className="font-semibold">
                              {row.last_price ?? "-"}
                              </span>
                              {(row.last_date || row.last_time) && (
                              <span className="text-gray-400 text-[11px] ml-1">
                                (
                                {row.last_date || row.trade_date}
                                {row.last_time ? ` ${row.last_time.slice(0, 5)}` : ""}
                                )
                              </span>
                              )}
                            </div>
                            
                            {/* CLOSE: harga + hanya tanggal */}
                            <div>
                              Close:{" "}
                              <span className="font-semibold">
                              {row.price != null ? fmtNum(row.price) : "-"}
                              </span>
                              {row.trade_date && (
                              <span className="text-gray-400 text-[11px] ml-1">
                                ({row.trade_date})
                              </span>
                              )}
                            </div>
                      
                            
                            {/* SIGNAL + TIMING (one line) */}
                            <div>
                              Signal:
                              <span className="font-semibold ml-1">{row.signal ?? "-"}</span>
                            
                              {(() => {
                              const timing = getTimingStatus(row);
                              return (
                                <span className={`ml-2 font-semibold ${timing.className}`}>
                                {timing.label}
                                </span>
                              );
                              })()}
                            </div>
                            
                            {/* BUY RANGE */}
                            <div>
                              {(() => {
                              const [low, high] = extractBuyRange(row);
                              if (low == null || high == null) return <>Buy: -</>;
                            
                              return <>Buy: {fmtFloor(low)} - {fmtFloor(high)}</>;
                              })()}
                            </div>
                            
                            </div>
                      
                        
                            <div className="text-right space-y-1">
                            <div className={getPctClass(row.chg_4w_pct)}>
                              4W: {row.chg_4w_pct != null ? fmtPct(row.chg_4w_pct) : "-"}
                            </div>
                            <div className={getPctClass(row.chg_13w_pct)}>
                              13W: {row.chg_13w_pct != null ? fmtPct(row.chg_13w_pct) : "-"}
                            </div>
                            <div>
                              RR: {row.rr != null ? row.rr : "-"}
                            </div>
                            </div>
                          </div>
                        
                          <div className="flex justify-between items-center mt-2 text-[11px]">
                            <div>
                            Suggest: {row.suggest_pct != null ? fmtPct(row.suggest_pct) : "-"}
                            </div>
                            <button
	                            onClick={() => handleAskAi(row, ASK_AI_TYPES.FULL_ANALYSIS)}
	                            className="px-3 py-1 rounded bg-indigo-600 hover:bg-indigo-500 text-white font-semibold text-[11px]"
								title="Analisa lengkap saham (AI)"
                            >
                            Ask AI
                            </button>
                          </div>
                          </div>
                        ))}
                      </div>
                      {/* ========== PAGINATION (Sekarang berada di dalam Mobile Wrapper) ========== */}
                    <PaginationBar />
                    </div>                  
                  </>                              
              )}

              {!loading &&
                (
                  (groupBy === "all" && rows.length === 0) ||
                  (groupBy === "industry" && selectedIndustry && rows.length === 0) ||
                  (groupBy === "konglo" && selectedKonglo && rows.length === 0)
                ) && (
                  <div className="text-gray-400 mt-2">
                    Belum ada data. Upload Ringkasan Saham BEI & Stock Screener
                    di sidebar terlebih dahulu.
                  </div>
              )}
             
            </>

          ) : (
              
              // ===== DETAIL VIEW (RESPONSIVE) =====
				    <div className="mt-1 mx-auto w-full max-w-[1400px] px-4 md:px-6 lg:px-8 flex flex-col gap-3">					
              {/* 🔝 Header detail – (Tetap Responsif) */}
              <div className="sticky top-0 z-10 bg-gray-900/95 backdrop-blur border-b border-gray-800 pb-2">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                  {/* Left: Back + Title */}
					        <div className="flex items-center gap-2 px-1 py-2">
                    {!isForcedDetail && (
                      <button
                        onClick={() => {
                          const isDesktop = window.matchMedia("(min-width: 768px)").matches;
                          if (isDesktop) {
                            setSelectedCode(null);   // ✅ balik ke list di panel kanan
                            setView("list");
                          } else {
                            nav(-1);                 // ✅ mobile tetap pakai page navigation
                          }
                        }}
                        className="px-3 py-1 rounded bg-gray-700 hover:bg-gray-600 text-gray-100 text-xs md:text-sm"
                        title="Kembali"
                      >
                        ← Back
                      </button>
                    )}

                    <div className="flex flex-col">
                      <h3 className="text-gray-100 font-semibold text-sm md:text-base">
                        {actualSelectedCode}
                      </h3>
                    </div>

                    <button
                      onClick={() => {
                        if (!selectedCode) return;
                        if (isStarred) handleRemove(selectedCode);
                        else handleAdd(selectedCode);
                      }}
                      className={`h-8 w-8 flex items-center justify-center rounded border transition
                        ${isStarred
                          ? "bg-yellow-500/20 border-yellow-500 text-yellow-300"
                          : "bg-gray-800 border-gray-700 text-gray-400 hover:bg-gray-700"}
                      `}
                      title={isStarred ? "Remove from Watchlist" : "Add to Watchlist"}
                    >
                      ★
                    </button>

                  </div>
                </div>
              </div>
                  
              {/* 📈 TradingView Chart (Tetap Responsif) */}
              <div className="rounded-lg border border-gray-700 overflow-hidden bg-gray-900">
                <iframe
                  key={selectedCode}
                  title={`TV-${selectedCode}`}
                  src={makeTvUrl(selectedCode)}
                  loading="lazy"
                  // Tinggi disesuaikan secara responsif
                  className="block w-full h-[260px] md:h-[320px] bg-gray-900" 
                  referrerPolicy="no-referrer-when-downgrade"
                  sandbox="allow-same-origin allow-scripts allow-popups allow-popups-to-escape-sandbox"
                />
              </div>
                  
              {/* 📊 Summary + Mini backtest + timing (Tetap Responsif) */}
              {/* Di desktop, asumsikan MiniBacktestPanel dan TimingCheckPanel akan bersebelahan.
                  Jika Anda ingin bersebelahan di desktop, ganti grid-cols-1 dengan grid-cols-1 md:grid-cols-2 */}
              <div className="grid grid-cols-1 md:grid-cols-1 gap-4 mt-1">
				<StockDetailSummaryBar
				  row={summaryRow}
				  onAskAi={(row) => handleAskAi(row, ASK_AI_TYPES.QUICK_SUMMARY)}
				  getTimingStatus={getTimingStatus}
				  extractBuyRange={extractBuyRange}
				  fmtPct={fmtPct}
				  fmtFloor={fmtFloor}
				  getPctClass={getPctClass}
				/>
								  
				<MiniBacktestPanel code={selectedCode} limitDays={limitDays} />

                <BrokerSummaryCard code={selectedCode} />
        
                {/* <TimingCheckPanel code={selectedCode} /> */}
              </div>
                  
              {/* 📋 Tabel transaksi – PENGGABUNGAN VERSI MOBILE & DESKTOP */}
              <div className="pt-2"> 
                {detailLoading ? (
                  <div className="text-sm text-amber-300">Loading detail…</div>
                    ) : detailRows.length === 0 ? (
                        <div className="text-sm text-gray-400">Tidak ada data.</div>
                    ) : (
                    <>
                      {/* ======================================================= */}
                      {/* TAMPILAN MOBILE (KOLOM MINIMALIS)          */}
                      {/* ======================================================= */}
                      <div className="md:hidden overflow-x-auto rounded-lg border border-gray-700">
                        <h4 className="text-gray-100 font-semibold text-sm mb-2 px-1">Riwayat Transaksi (Mobile View)</h4>
                        <table className="min-w-full table-auto text-xs">
                          <thead className="sticky top-0 bg-gray-700 text-gray-200 z-10">
                            <tr>
                              <th className="py-2 pr-2 pl-3 text-left w-[25%]">Date</th> 
                              <th className="py-2 pr-2 text-right w-[15%]">Prev</th>
                              <th className="py-2 pr-2 text-right w-[15%]">Close</th> 
                              <th className="py-2 pr-2 text-right w-[15%]">%Chg</th>
                              <th className="py-2 pr-3 text-right w-[30%]">F Nett</th> 
                            </tr>
                          </thead>
                          <tbody>
                            {[...detailRows]
                              .sort((a, b) => new Date(b.date) - new Date(a.date))
                              .map((r) => {
                                const foreignNett = (r.foreign_buy || 0) - (r.foreign_sell || 0); // Hitung F Nett
                                const isFBuy = foreignNett > 0;
                                const isFSell = foreignNett < 0;
                                
                                return (
                                  <tr
                                    key={`${r.symbol}-${r.date}-mobile`}
                                    className="border-b border-gray-700 odd:bg-gray-800 even:bg-gray-900 hover:bg-gray-700 transition"
                                  >
                                    <td className="px-3 py-2 text-gray-200 whitespace-nowrap">{r.date}</td>
                                    <td className="py-2 pr-2 text-right text-gray-200">
                                      {fmtNum(r.prev)}
                                    </td>
                                    <td className="py-2 pr-2 text-right text-gray-200">
                                      {fmtNum(r.close)}
                                    </td>
                                    <td
                                      className={`py-2 pr-2 text-right ${
                                        r.pct < 0
                                          ? "text-red-400"
                                          : r.pct > 0
                                          ? "text-green-400"
                                          : "text-gray-300"
                                      }`}
                                    >
                                      {fmtNum(r.pct)}
                                    </td>
                                    <td className={`py-2 pr-3 text-right ${
                                        isFBuy ? "text-green-400" : isFSell ? "text-red-400" : "text-gray-300"
                                    }`}>
                                      {fmtInt(foreignNett)}
                                    </td>
                                  </tr>
                                )
                              })}
                          </tbody>
                        </table>
                      </div>
        
                      {/* ======================================================= */}
                      {/* TAMPILAN DESKTOP (SEMUA KOLOM)             */}
                      {/* ======================================================= */}
                      <div className="hidden md:block overflow-x-auto rounded-lg border border-gray-700">

                        <div className="flex items-center justify-between mb-2">
                          <div className="text-sm font-semibold text-gray-200">
                            Riwayat Transaksi (Desktop View)
                          </div>

                          <div className="flex items-center gap-2">
                            <input
                              type="number"
                              value={detailDays}
                              onChange={(e) => setDetailDays(Number(e.target.value) || 0)}
                              className="w-16 md:w-20 bg-gray-800 border border-gray-700 rounded px-2 py-1 text-gray-100 text-xs md:text-sm"
                              title="Days"
                            />

                            <button
                              onClick={() => selectedCode && fetchDetail(selectedCode, detailYear, detailDays)}
                              className="px-3 py-1 rounded bg-blue-600 hover:bg-blue-500 text-white text-xs md:text-sm"
                            >
                              Refresh
                            </button>
                          </div>
                        </div>
                        
                        {/* <h4 className="text-gray-100 font-semibold text-sm mb-2 px-1">Riwayat Transaksi (Desktop View)</h4> */}
                        <table className="min-w-full text-xs">
                          <thead className="sticky top-0 bg-gray-700 text-gray-200 z-10">
                            <tr>
                              <th className="py-2 pr-3 pl-3 text-left">Date</th>
                              <th className="py-2 pr-3 text-right">Prev</th>
                              <th className="py-2 pr-3 text-right">Open</th>
                              <th className="py-2 pr-3 text-right">High</th>
                              <th className="py-2 pr-3 text-right">Low</th>
                              <th className="py-2 pr-3 text-right">Close</th>
                              <th className="py-2 pr-3 text-right">%Chg</th>
                              <th className="py-2 pr-3 text-right">Vol</th>
                              <th className="py-2 pr-3 text-right">Value</th>
                              <th className="py-2 pr-3 text-right">Freq</th>
                              <th className="py-2 pr-3 text-right">F Nett</th>
                            </tr>
                          </thead>
                          <tbody>
                            {[...detailRows]
                              .sort((a, b) => new Date(b.date) - new Date(a.date))
                              .map((r) => {
                                const foreignNett = r.foreign_buy - r.foreign_sell; 
                                const isFBuy = foreignNett > 0;
                                const isFSell = foreignNett < 0;
                        
                                return (
                                  <tr
                                    key={`${r.symbol}-${r.date}-desktop`}
                                    className="border-b border-gray-700 odd:bg-gray-800 even:bg-gray-900 hover:bg-gray-700 transition"
                                  >
                                    <td className="px-3 py-2 text-gray-200">{r.date}</td>
                                    <td className="py-2 pr-3 text-right text-gray-200">{fmtNum(r.prev)}</td>
                                    <td className="py-2 pr-3 text-right text-gray-200">{fmtNum(r.open)}</td>
                                    <td className="py-2 pr-3 text-right text-gray-200">{fmtNum(r.high)}</td>
                                    <td className="py-2 pr-3 text-right text-gray-200">{fmtNum(r.low)}</td>
                                    <td className="py-2 pr-3 text-right text-gray-200">{fmtNum(r.close)}</td>
                                    <td className={`py-2 pr-3 text-right ${
                                        r.pct < 0 ? "text-red-400" : r.pct > 0 ? "text-green-400" : "text-gray-300"
                                      }`}
                                    >
                                      {fmtNum(r.pct)}
                                    </td>
                                    <td className="py-2 pr-3 text-right text-gray-200">{fmtInt(r.volume)}</td>
                                    <td className="py-2 pr-3 text-right text-gray-200">{fmtInt(r.value)}</td>
                                    <td className="py-2 pr-3 text-right text-gray-200">{fmtInt(r.freq)}</td>
                                    {/* Kolom Foreign Nett */}
                                    <td className={`py-2 pr-3 text-right ${isFBuy ? "text-green-400" : isFSell ? "text-red-400" : "text-gray-300"}`}>
                                      {fmtInt(foreignNett)}
                                    </td>
                                  </tr>
                                );
                              })}
                          </tbody>
                        </table>
                      </div>
                    </>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
      <Toast />
    </>
  );
}
